import {
  ComponentType,
  createContext,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreviousValue } from '@atom/hooks/usePreviousValue';
import { ResumeData } from '@atom/types/ai';

import {
  useAIChatAttachments,
  UseAIChatAttachmentsResponse,
} from './hooks/useChatAttachments';
import { useChatUI, UseChatUIResponse } from './hooks/useChatUI';
import { useFeedback, UseFeedbackResponse } from './hooks/useFeedback';
import {
  useImpersonation,
  UseImpersonationResponse,
} from './hooks/useImpersonation';
import { useMessages, UseMessagesResponse } from './hooks/useMessages';
import {
  useModelSelection,
  UseModelSelectionResponse,
} from './hooks/useModelSelection';
import { useThreads, UseThreadsResponse } from './hooks/useThreads';
import ChatRuntime from './ChatRuntime';

export type PendingResume = {
  runId: string;
  toolName: string;
  resumeData: ResumeData;
};

export type ChatContextValue = {
  threadId: string;
  threadsData: UseThreadsResponse;
  messagesData: UseMessagesResponse;
  ui: UseChatUIResponse;
  feedbackData: UseFeedbackResponse;
  attachmentsData: UseAIChatAttachmentsResponse;
  impersonationData: UseImpersonationResponse;
  modelSelectionData: UseModelSelectionResponse;
  resume: MutableRefObject<PendingResume | null>;
  setResume: (resume: PendingResume | null) => void;
  error: Error | null;
  onError: (error: Error | null) => void;
};

const NOOP: () => any = () => null;

const initialContext: ChatContextValue = {
  threadId: '',
  threadsData: {
    threads: [],
    currentThread: null,
    loading: true,
    error: null,
    hasMoreThreads: false,
    fetchMoreThreads: NOOP,
    refreshThread: NOOP,
    refreshingThread: false,
    updateThread: NOOP,
    createThread: NOOP,
    deleteThread: NOOP,
    startNewThread: NOOP,
  },
  messagesData: {
    messages: [],
    loading: false,
    error: null,
  },
  ui: {
    drawerCollapsed: false,
    toggleDrawerCollapsed: NOOP,
    chatOpen: false,
    chatExpanded: false,
    toggleChatOpen: NOOP,
    toggleChatExpanded: NOOP,
    loading: false,
    setLoading: NOOP,
  },
  feedbackData: {
    feedback: {},
    loading: false,
    error: null,
    onFeedbackGiven: NOOP,
  },
  attachmentsData: {
    stagedAttachments: [],
    attachments: {},
    stageAttachments: async () => {},
    removeStagedAttachment: NOOP,
    clearStagedAttachments: NOOP,
    getStagedAttachments: () => [],
    addAttachment: NOOP,
    isUploading: false,
  },
  impersonationData: {
    impersonatedUser: null,
    setImpersonatedUser: NOOP,
    clearImpersonation: NOOP,
    getImpersonatedUserId: () => null,
  },
  modelSelectionData: {
    model: null,
    scope: 'agent',
    setModel: NOOP,
    setScope: NOOP,
    clear: NOOP,
    getModel: () => null,
    getScope: () => 'agent',
  },
  resume: { current: null },
  setResume: NOOP,
  error: null,
  onError: NOOP,
};

const ChatContext = createContext<ChatContextValue>(initialContext);

export const useChatContext = () => useContext(ChatContext);

export function withChatContext<T>(Component: ComponentType<T>) {
  return (props: T) => {
    const [initialized, setInitialized] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const resume = useRef<PendingResume | null>(null);
    const setResume = useCallback((value: PendingResume | null) => {
      resume.current = value;
    }, []);
    const [{ threadId: threadIdParam }] = useNavigation({
      threadId: SearchParamType.STRING,
    });
    const threadId = threadIdParam ?? '';
    const threadsData = useThreads(threadId);
    const messagesData = useMessages(threadId);
    const feedbackData = useFeedback(threadId);
    const ui = useChatUI();
    const attachmentsData = useAIChatAttachments(threadId);
    const impersonationData = useImpersonation();
    const modelSelectionData = useModelSelection();
    const previousThreadId = usePreviousValue(threadId);
    const { startNewThread } = threadsData;

    useEffect(() => {
      if (!threadId) {
        startNewThread();
      }
    }, [threadId, startNewThread]);

    useEffect(() => {
      if (previousThreadId !== threadId) {
        setError(null);
      }
    }, [previousThreadId, threadId, setError]);

    const onError = useCallback((err: Error | null) => {
      setError(err);
    }, []);

    const context = useMemo<ChatContextValue>(
      () => ({
        threadId,
        threadsData,
        messagesData,
        feedbackData,
        attachmentsData,
        impersonationData,
        modelSelectionData,
        ui,
        resume,
        setResume,
        error,
        onError,
      }),
      [
        threadId,
        threadsData,
        messagesData,
        feedbackData,
        attachmentsData,
        impersonationData,
        modelSelectionData,
        ui,
        setResume,
        error,
        onError,
      ],
    );

    const loading = messagesData.loading || feedbackData.loading;

    useEffect(() => {
      if (!loading) {
        setInitialized(true);
      }
    }, [loading]);

    useEffect(() => {
      setInitialized(false);
    }, [threadId]);

    // we want to re-init the runtime after messages have finished loading and are loaded into state but then NOT AGAIN after that
    // once initial messages are hydrated assistant-ui will handle everything in state
    const key = useMemo(
      () => `${threadId}${initialized ? '' : '-loading'}`,
      [threadId, initialized],
    );

    if (!threadId) {
      return null;
    }

    return (
      <ChatContext.Provider value={context}>
        <ChatRuntime key={key}>
          <Component {...(props as any)} />
        </ChatRuntime>
      </ChatContext.Provider>
    );
  };
}
